import { useSortable } from "@dnd-kit/sortable";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Box, IconButton, Typography } from "@mui/material";
import clsx from "clsx";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import star from "../../../../../../../assets/images/pngs/activeStar.png";
import LoadingPlaceholder from "../../../../../../../components/common/LoadingPlaceholder/LoadingPlaceholder.tsx";
import useHoverOverlay from "../../../../../../../hooks/useHoverOverlay.tsx";
import { isAdsEditor } from "../../../../../../../utils/index.ts";
import duplicate from "../../../../../../assets/svgs/Duplicate.svg";
import starIcon from "../../../../../../assets/svgs/star.png";
import trash from "../../../../../../assets/svgs/trash.svg";
import useDesignEditorContext from "../../../../../../hooks/useDesignEditorContext";
import type { IScene } from "../../../../../../types";
import useSceneOperations from "../Scenes/utils/SceneOperations.ts";
import styles from "./SceneItem.module.scss";
import SceneItemMenu from "./SceneItemMenu/SceneItemMenu.tsx";
interface Props {
	isCurrentScene: boolean;
	scene: IScene;
	preview: string;
	index: number;
	changePage: (p: IScene) => void;
	containerClassName?: string;
	isLoadingSceneItem?: boolean;
	onSetMainScene?: () => void;
}
interface IMenuItem {
	label: string;
	icon: string;
	onClick: () => void;
}
const SceneItem = ({
	isCurrentScene,
	scene,
	preview,
	changePage,
	containerClassName = "",
	isLoadingSceneItem = false,
	onSetMainScene,
	index,
}: Props) => {
	const { attributes, listeners, setNodeRef } = useSortable({ id: scene.id });
	const { t } = useTranslation("translation", { keyPrefix: "campaignTab.scenes" });
	const sceneRef = React.useRef<HTMLDivElement>(null);
	const { setContextMenuTimelineRequest, contextMenuTimelineRequest } = useDesignEditorContext();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [hoverRef, isHovered, setIsHovered] = useHoverOverlay();
	const { deleteScene, duplicateScene, downloadScene } = useSceneOperations();

	const rawMenuItems: (IMenuItem | null)[] = [
		{
			label: t("duplicateScene"),
			icon: duplicate,
			onClick: () => {
				duplicateScene(undefined);
			},
		},
		!isAdsEditor()
			? {
					label: t("setAsMainScene"),
					icon: starIcon,
					onClick: () => {
						onSetMainScene?.();
					},
				}
			: null,
		isAdsEditor()
			? {
					label: t("download"),
					icon: starIcon,
					onClick: () => {
						downloadScene();
					},
				}
			: null,
		{
			label: t("deleteScene"),
			icon: trash,
			onClick: () => {
				deleteScene(undefined);
			},
		},
	];

	const menuItems: IMenuItem[] = rawMenuItems.filter((item): item is IMenuItem => item !== null);

	React.useEffect(() => {
		const timeLineItemDiv = sceneRef.current;
		const handleContextMenu = (event: MouseEvent) => {
			event.preventDefault();
			setContextMenuTimelineRequest({
				id: scene.id,
				left: event.pageX,
				top: event.pageY,
				visible: true,
			});
		};
		if (timeLineItemDiv) {
			timeLineItemDiv.addEventListener("contextmenu", handleContextMenu);
		}
		return () => {
			if (timeLineItemDiv) {
				timeLineItemDiv.removeEventListener("contextmenu", handleContextMenu);
			}
		};
	}, [sceneRef, scene, anchorEl]);

	const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
		setContextMenuTimelineRequest({
			id: scene.id,
			left: event.pageX,
			top: event.pageY,
			visible: true,
		});
	};
	const handleMenuClose = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(null);
		setContextMenuTimelineRequest({ ...contextMenuTimelineRequest, visible: false });

		event.stopPropagation();
	};

	return (
		<Box
			ref={setNodeRef}
			{...attributes}
			{...listeners}
			className={clsx(styles.sceneItemContainer, containerClassName, {
				[styles.isCurrentScene]: isCurrentScene,
			})}
			onClick={() => {
				if (!isCurrentScene) {
					changePage(scene);
				}
			}}
		>
			<LoadingPlaceholder
				className={styles.circleLoader}
				size="25px"
				isLoading={isCurrentScene && isLoadingSceneItem}
			/>
			<Box ref={hoverRef}>
				{scene.isMainScene && <img className={styles.defaultSceneImg} src={star} alt="star" />}
				<IconButton
					className={styles.iconButton}
					onClick={handleMenuOpen}
					style={{ visibility: isHovered || Boolean(anchorEl) ? "visible" : "hidden" }}
				>
					<MoreHorizIcon className={styles.MoreHorizIcon} />
				</IconButton>
				<Box
					ref={sceneRef}
					className={clsx(styles.sceneImage, {
						[styles.loading]: isCurrentScene && isLoadingSceneItem,
					})}
				>
					<img src={preview} />
				</Box>
				<Typography className={styles.sceneName}>{"Scene " + (index + 1)}</Typography>
				<Typography className={styles.sceneName}>
					{t("key", { width: scene.frame?.width, height: scene.frame?.height })}
				</Typography>

				<SceneItemMenu
					anchorEl={anchorEl}
					onClose={handleMenuClose}
					menuItems={menuItems}
					sceneName={scene.name ?? t("untitled")}
					width={scene.frame?.width}
					height={scene.frame?.height}
					setIsHovered={setIsHovered}
					isMainScene={scene.isMainScene}
				/>
			</Box>
		</Box>
	);
};

export default SceneItem;
